// DashboardApi.ts
import axios from 'axios';
import apiConfig from '../pages/apiConfig.ts';

const API_BASE_URL = apiConfig.apiBaseURL;

// Dashboard interfaces
export interface PaidParentGiftCounts {
  totalPaidParentGifts: number;
  statusBreakdown: {
    upcoming: number;
    processing: number;
    shipped: number;
    delivered: number;
  };
  calculatedTotal: number;
  isConsistent: boolean;
}

export interface UserStatusCounts {
  totalUsers: number;
  statusBreakdown: {
    active: number;
    inactive: number;
    suspended: number;
  };
  calculatedTotal: number;
  isConsistent: boolean;
}

export interface MonthlyRevenue {
  year: number;
  month: number;
  monthName: string;
  giftRevenue: number;
  giftCount: number;
  subscriptionRevenue: number;
  subscriptionCount: number;
  totalRevenue: number;
}

export interface RevenueData {
  totalRevenue: number;
  revenueBreakdown: {
    giftPayments: number;
    subscriptionPayments: number;
  };
  transactionCounts: {
    paidGifts: number;
    subscriptions: number;
    totalTransactions: number;
  };
  monthlyRevenue: MonthlyRevenue[];
}

export interface TopState {
  stateName: string;
  count: number;
}

export interface TopStatesData {
  topStates: TopState[];
}

export interface DailySubscription {
  date: string;
  count: number;
}

export interface SubscriptionData {
  totalSubscriptions: number;
  dateRange: {
    startDate: string;
    endDate: string;
  };
  dailyBreakdown: DailySubscription[];
}

// API Response interfaces
export interface PaidParentGiftCountsResponse {
  status: 'Success' | 'Failed';
  message: string;
  data?: PaidParentGiftCounts;
}

export interface UserStatusCountsResponse {
  status: 'Success' | 'Failed';
  message: string;
  data?: UserStatusCounts;
}

export interface TotalRevenueResponse {
  status: 'Success' | 'Failed';
  message: string;
  data?: RevenueData;
}

export interface TopStatesByUsersResponse {
  status: 'Success' | 'Failed';
  message: string;
  data?: TopStatesData;
}

export interface SubscriptionCounts15DaysResponse {
  status: 'Success' | 'Failed';
  message: string;
  data?: SubscriptionData;
}

// Helper function to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken');
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` })
  };
};

export const dashboardApi = {
  // Get paid parent gift counts
  getPaidParentGiftCounts: async (): Promise<PaidParentGiftCountsResponse> => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/dashboard/paid-parent-gift-counts`,
        {
          headers: getAuthHeaders()
        }
      );

      return response.data;
    } catch (error: any) {
      console.error('Error fetching paid parent gift counts:', error);
      return {
        status: 'Failed',
        message: error.response?.data?.message || 'Failed to fetch gift counts',
        data: {
          totalPaidParentGifts: 0,
          statusBreakdown: {
            upcoming: 0,
            processing: 0,
            shipped: 0,
            delivered: 0
          },
          calculatedTotal: 0,
          isConsistent: true
        }
      };
    }
  },

  // Get user status counts
  getUserStatusCounts: async (): Promise<UserStatusCountsResponse> => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/dashboard/user-status-counts`,
        {
          headers: getAuthHeaders()
        }
      );

      return response.data;
    } catch (error: any) {
      console.error('Error fetching user status counts:', error);
      return {
        status: 'Failed',
        message: error.response?.data?.message || 'Failed to fetch user status counts',
        data: {
          totalUsers: 0,
          statusBreakdown: {
            active: 0,
            inactive: 0,
            suspended: 0
          },
          calculatedTotal: 0,
          isConsistent: true
        }
      };
    }
  },

  // Get total revenue
  getTotalRevenue: async (): Promise<TotalRevenueResponse> => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/dashboard/total-revenue`,
        {
          headers: getAuthHeaders()
        }
      );

      return response.data;
    } catch (error: any) {
      console.error('Error fetching total revenue:', error);
      return {
        status: 'Failed',
        message: error.response?.data?.message || 'Failed to fetch total revenue',
        data: {
          totalRevenue: 0,
          revenueBreakdown: {
            giftPayments: 0,
            subscriptionPayments: 0
          },
          transactionCounts: {
            paidGifts: 0,
            subscriptions: 0,
            totalTransactions: 0
          },
          monthlyRevenue: []
        }
      };
    }
  },

  // Get top states by users
  getTopStatesByUsers: async (): Promise<TopStatesByUsersResponse> => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/dashboard/top-states-by-users`,
        {
          headers: getAuthHeaders()
        }
      );

      return response.data;
    } catch (error: any) {
      console.error('Error fetching top states by users:', error);
      return {
        status: 'Failed',
        message: error.response?.data?.message || 'Failed to fetch top states',
        data: {
          topStates: []
        }
      };
    }
  },

  // Get subscription counts for 15 days
  getSubscriptionCounts15Days: async (): Promise<SubscriptionCounts15DaysResponse> => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/dashboard/subscription-counts-15-days`,
        {
          headers: getAuthHeaders()
        }
      );

      return response.data;
    } catch (error: any) {
      console.error('Error fetching subscription counts:', error);
      return {
        status: 'Failed',
        message: error.response?.data?.message || 'Failed to fetch subscription counts',
        data: {
          totalSubscriptions: 0,
          dateRange: {
            startDate: '',
            endDate: ''
          },
          dailyBreakdown: []
        }
      };
    }
  }
};
